import { useFlowSessionLiveStatus } from "@/hooks/useFlowSessionLiveStatus";
import { Process } from "@/pages/canvas/context/CanvasProcessContext";
import { BaseEntityCardProcess } from "@cohesive/ui-components";
import { AlertCircle } from "lucide-react";

interface LiveProcessProps {
  /** The process to render - from CanvasProcessContext which includes flowSessionId */
  process: Process;
  /** Callback when the process is clicked - receives the flow session ID */
  onNavigate?: (flowSessionId: string) => void;
  /** Additional className for styling */
  className?: string;
  /** If true, hide the process when it reaches "complete" status */
  hideWhenComplete?: boolean;
}

/**
 * A component that renders a process indicator with live status updates.
 * Uses the useFlowSessionLiveStatus hook to get real-time status from the flow session.
 *
 * Features:
 * - Shows live status (pending, processing, needs_attention, complete, error)
 * - Displays current activity text when available
 * - Handles click navigation to flow session
 * - Falls back to static process status if no flowSessionId
 * - Can auto-hide when complete (useful for node display, but not for process tray)
 */
export function LiveProcess({
  process,
  onNavigate,
  className,
  hideWhenComplete = false,
}: LiveProcessProps) {
  const { status, processStatus, currentActivity } = useFlowSessionLiveStatus(
    process.flowSessionId,
  );

  // Use live status if we have a flow session, otherwise fall back to process status
  const displayStatus = process.flowSessionId ? processStatus : process.status;

  // Hide completed processes if requested
  if (hideWhenComplete && displayStatus === "complete") {
    return null;
  }

  // Use current activity as label if available, otherwise use process label
  const displayLabel = currentActivity || process.label;

  // Custom icon for needs_attention status
  const customIcon =
    status === "needs_attention" ? (
      <AlertCircle className="h-4 w-4 text-lc-blocked" />
    ) : undefined;

  // Handle click - navigate to flow session if available
  const handleClick = () => {
    if (process.flowSessionId && onNavigate) {
      onNavigate(process.flowSessionId);
    } else if (process.onClick) {
      process.onClick();
    }
  };

  return (
    <BaseEntityCardProcess
      id={process.id}
      label={displayLabel}
      status={displayStatus}
      icon={customIcon || process.icon}
      expandedContent={process.expandedContent}
      onClick={handleClick}
      className={className}
    />
  );
}

export default LiveProcess;
